import { BENEFIT_COST_KEYS, COST_KEYS } from "../../../constants/costConstants";
import DataGridCell from "../../../ui/DataGridCell";
import { Tooltip } from "@mui/material";

export const BC_CELL_STATUS = {
  VALID: "VALID",
  CONTROL_STRATEGY_NOT_PREFERRED: "CONTROL_STRATEGY_NOT_PREFERRED",
  CONTROL_STRATEGY_PREFERRED: "CONTROL_STRATEGY_PREFERRED",
  BENEFITS_AND_COSTS_LESS_THAN_BASE_CASE:
    "BENEFITS_AND_COSTS_LESS_THAN_BASE_CASE",
};

export const BC_CELL_STATUS_TO_FOOTNOTE = {
  CONTROL_STRATEGY_NOT_PREFERRED:
    "Control Strategy not preferred. Benefits are less than base case and cost is greater than base case.",
  CONTROL_STRATEGY_PREFERRED:
    "Control strategy preferred. Benefits are greater than base case and cost is less than base case.",
  BENEFITS_AND_COSTS_LESS_THAN_BASE_CASE:
    "Benefits are less than base case and cost is less than base case.",
};

export const getBcCellStatus = ({ bcValue, cost }) => {
  if (bcValue < 0 && cost > 0) {
    return BC_CELL_STATUS.CONTROL_STRATEGY_NOT_PREFERRED;
  }
  if (bcValue < 0 && cost < 0) {
    return BC_CELL_STATUS.CONTROL_STRATEGY_PREFERRED;
  }
  if (bcValue > 0 && cost < 0) {
    return BC_CELL_STATUS.BENEFITS_AND_COSTS_LESS_THAN_BASE_CASE;
  }
  return BC_CELL_STATUS.VALID;
};

export const output_total_cost_table_columns_formatted = [
  { label: "Intersection", field: COST_KEYS.INTERSECTION },
  {
    label: "Planning, Construction & ROW",
    field: COST_KEYS.PLANNING_CONSTRUCTION_ROW,
  },
  { label: "Post-Opening Costs", field: COST_KEYS.POST_OPENING },
  { label: "Auto Passenger Delay", field: COST_KEYS.AUTO_DELAY },
  { label: "Truck Delay", field: COST_KEYS.TRUCK_DELAY },
  { label: "Safety", field: COST_KEYS.SAFETY },
  { label: "Total Cost", field: COST_KEYS.TOTAL_COST },
].map(({ field, label }) => {
  return {
    field,
    headerName: label,
    minWidth: field === COST_KEYS.INTERSECTION ? 200 : undefined,
    type: field !== COST_KEYS.INTERSECTION ? "number" : undefined,
    renderCell: ({ value }) => (
      <DataGridCell
        style={{ fontWeight: field === COST_KEYS.TOTAL_COST ? 600 : 500 }}
        isDollar={field !== COST_KEYS.INTERSECTION}
        value={value}
      />
    ),
    valueGetter:
      field === COST_KEYS.TOTAL_COST
        ? (params) =>
            Object.values(params.row)
              .filter(
                (value, index) => index !== 0 && typeof value === "number"
              )
              .reduce((total, value) => total + value, 0)
        : undefined,
  };
});

const getIsBcField = (field) =>
  field === BENEFIT_COST_KEYS.BC_RATIO ||
  field === BENEFIT_COST_KEYS.DELAY_BC_RATIO ||
  field === BENEFIT_COST_KEYS.SAFETY_BC_RATIO;

export const getOutputBenefitCostTableFormatted = (footnotesOrder) => {
  return [
    { label: "Intersection", field: BENEFIT_COST_KEYS.INTERSECTION },
    { label: "Auto Passenger Delay", field: BENEFIT_COST_KEYS.AUTO_DELAY },
    { label: "Truck Delay", field: BENEFIT_COST_KEYS.TRUCK_DELAY },
    { label: "Safety", field: BENEFIT_COST_KEYS.SAFETY },
    {
      label: "Net Present Value of Benefits",
      field: BENEFIT_COST_KEYS.VALUE_OF_BENEFITS,
    },
    {
      label: "Net Present Value of Costs",
      field: BENEFIT_COST_KEYS.VALUE_OF_COSTS,
    },
    { label: "Benefit/Cost (B/C) Ratio", field: BENEFIT_COST_KEYS.BC_RATIO },
    { label: "Delay B/C", field: BENEFIT_COST_KEYS.DELAY_BC_RATIO },
    { label: "Safety B/C", field: BENEFIT_COST_KEYS.SAFETY_BC_RATIO },
  ].map(({ field, label }) => {
    return {
      field,
      headerName: label,
      minWidth: field === BENEFIT_COST_KEYS.INTERSECTION ? 200 : undefined,
      type: field === BENEFIT_COST_KEYS.INTERSECTION ? undefined : "number",
      renderCell: (params) => {
        const value = params.value;
        const cost = params.row[BENEFIT_COST_KEYS.VALUE_OF_COSTS];
        const isDollar =
          field !== BENEFIT_COST_KEYS.INTERSECTION &&
          field !== BENEFIT_COST_KEYS.DELAY_BC_RATIO &&
          field !== BENEFIT_COST_KEYS.SAFETY_BC_RATIO &&
          field !== BENEFIT_COST_KEYS.BC_RATIO;

        return getIsBcField(field) ? (
          getBcCellFormatted({ value, cost, field, footnotesOrder })
        ) : (
          <DataGridCell
            style={{
              fontWeight: 500,
            }}
            isDollar={isDollar}
            value={value}
          />
        );
      },
    };
  });
};

export function getBcCellFormatted({ value, cost, field, footnotesOrder }) {
  const bcStatus = getBcCellStatus({
    bcValue: value,
    cost,
  });
  const footnoteNumber = footnotesOrder.indexOf(bcStatus) + 1;
  const BC_CELL_STATUS_TO_DISPLAY_TEXT = {
    CONTROL_STRATEGY_NOT_PREFERRED: "Not preferred",
    CONTROL_STRATEGY_PREFERRED: "Preferred",
  };
  // Replace B/C value with text if needed and format number
  const displayBcValue =
    BC_CELL_STATUS_TO_DISPLAY_TEXT[bcStatus] ??
    (cost === 0 ? "0.00" : parseFloat(value).toFixed(2).toLocaleString());
  return footnoteNumber === 0 ? (
    // no footnote
    <DataGridCell
      style={{
        fontWeight: field === BENEFIT_COST_KEYS.BC_RATIO ? 600 : 500,
      }}
      isDollar={false}
      value={displayBcValue}
    />
  ) : (
    // add footnote in tooltip
    <Tooltip
      title={BC_CELL_STATUS_TO_FOOTNOTE[bcStatus]}
      data-testid={"tooltip"}
    >
      <div>
        <DataGridCell
          style={{
            fontWeight: field === BENEFIT_COST_KEYS.BC_RATIO ? 600 : 500,
          }}
          isDollar={false}
          value={displayBcValue}
        />
        <sup>{footnoteNumber}</sup>
      </div>
    </Tooltip>
  );
}
